import Encoding from "encoding-japanese";
import Papa from "papaparse";

export interface TransformSummary {
  fileName: string;
  downloadName: string;
  rowCount: number;
  minColumns: number;
  maxColumns: number;
  changedColumn28: number;
  clearedColumn30: number;
}

export interface TransformResult {
  blob: Blob;
  summary: TransformSummary;
}

const TARGET_COLUMN_INDEX = 27;
const CLEAR_COLUMN_INDEX = 29;
const REQUIRED_COLUMNS = 30;
const OUTPUT_VALUE = "ホビー";

export class CsvTransformError extends Error {
  constructor(
    message: string,
    public readonly details: string[] = []
  ) {
    super(message);
    this.name = "CsvTransformError";
  }
}

export function makeDownloadName(fileName: string): string {
  const cleaned = fileName.trim() || "converted.csv";
  return cleaned.replace(/\.csv$/i, "") + "_converted.csv";
}

export function decodeShiftJis(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  return Encoding.convert(bytes, {
    from: "SJIS",
    to: "UNICODE",
    type: "string"
  }) as string;
}

export function encodeShiftJis(text: string): Uint8Array {
  const unicodeCodes = Encoding.stringToCode(text);
  const sjisCodes = Encoding.convert(unicodeCodes, {
    from: "UNICODE",
    to: "SJIS"
  }) as number[];
  return new Uint8Array(sjisCodes);
}

export function transformCsvText(csvText: string, fileName: string): { csvText: string; summary: TransformSummary } {
  const parsed = Papa.parse<string[]>(csvText, {
    delimiter: ",",
    quoteChar: '"',
    escapeChar: '"',
    skipEmptyLines: false
  });

  if (parsed.errors.length > 0) {
    const details = parsed.errors.slice(0, 5).map((error) => {
      const rowLabel = typeof error.row === "number" ? `${error.row + 1}行目: ` : "";
      return `${rowLabel}${error.message}`;
    });
    throw new CsvTransformError("CSVの解析に失敗しました。", details);
  }

  const rows = parsed.data;
  if (/(?:\r\n|\n|\r)$/.test(csvText) && rows.at(-1)?.length === 1 && rows.at(-1)?.[0] === "") {
    rows.pop();
  }

  if (rows.length === 0) {
    throw new CsvTransformError("CSVに変換対象の行がありません。");
  }

  const shortRows = rows
    .map((row, index) => ({ rowNumber: index + 1, columnCount: row.length }))
    .filter((row) => row.columnCount < REQUIRED_COLUMNS);

  if (shortRows.length > 0) {
    const details = shortRows
      .slice(0, 10)
      .map((row) => `${row.rowNumber}行目: ${row.columnCount}列`);
    throw new CsvTransformError("30列未満の行があるため出力できません。", details);
  }

  let changedColumn28 = 0;
  let clearedColumn30 = 0;

  for (const row of rows) {
    row[TARGET_COLUMN_INDEX] = OUTPUT_VALUE;
    row[CLEAR_COLUMN_INDEX] = "";
    changedColumn28 += 1;
    clearedColumn30 += 1;
  }

  const outputCsv = Papa.unparse(rows, {
    delimiter: ",",
    newline: "\r\n",
    quoteChar: '"',
    escapeChar: '"'
  });

  const columnCounts = rows.map((row) => row.length);
  return {
    csvText: outputCsv,
    summary: {
      fileName,
      downloadName: makeDownloadName(fileName),
      rowCount: rows.length,
      minColumns: Math.min(...columnCounts),
      maxColumns: Math.max(...columnCounts),
      changedColumn28,
      clearedColumn30
    }
  };
}

export async function transformCsvFile(file: File): Promise<TransformResult> {
  const buffer = await file.arrayBuffer();
  const decoded = decodeShiftJis(buffer);
  const { csvText, summary } = transformCsvText(decoded, file.name);
  const encoded = encodeShiftJis(csvText);
  const blobPart = encoded.buffer.slice(
    encoded.byteOffset,
    encoded.byteOffset + encoded.byteLength
  ) as ArrayBuffer;
  return {
    blob: new Blob([blobPart], { type: "text/csv;charset=shift_jis" }),
    summary
  };
}
